import type {
  WorksheetGenericLanguageModuleType,
  WorksheetModule,
  WorksheetSize,
} from "@arbeidsark/shared"
import { useMemo, useState } from "react"
import styled, { useTheme } from "styled-components"
import { OutlineButton } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import GenericLanguageTaskTypesSelect from "../../selects/GenericLanguageTaskTypesSelect"
import WordlistModal from "./WordlistModal"

interface LayoutProps {
  layout: WorksheetSize
}

const Layout = styled.div<LayoutProps>`
  padding: 1.5rem 1.25rem;

  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Label = styled.label`
  font-size: 14px;
  display: inline-block;
`

export interface GenericLanguageModuleConfigurationCellProps {
  pageIndex: number
  moduleIndex: number
  layout: WorksheetSize
  configuration?: WorksheetModule
  onConfigurationChanged: (configuration: WorksheetModule) => void
  onRegenerateModule: () => void
  isFirstModule?: boolean
}

const GenericLanguageModuleConfigurationCell: React.FC<
  GenericLanguageModuleConfigurationCellProps
> = ({
  pageIndex,
  moduleIndex,
  configuration,
  layout,
  onConfigurationChanged,
  onRegenerateModule,
  isFirstModule = false,
}) => {
  const [detailConfigurationIsOpen, setDetailConfigurationIsOpen] =
    useState(false)

  const theme = useTheme()

  const translations = useTranslate({
    task: "worksheets.labels.task",
    configure: "worksheets.labels.configure",
    regenerate: "worksheets.buttons.regenerate",
    errors: {
      readingTextsNeedFullLayout:
        "errors.worksheets.readingtexts-need-full-layout",
    },
  })

  const error = useMemo(() => {
    if (configuration?.type === "generic/readingTexts" && layout !== "full") {
      return translations.errors.readingTextsNeedFullLayout
    }

    return null
  }, [configuration, translations, layout])

  return (
    <Layout layout={layout}>
      <Label>
        {translations.task} {moduleIndex + 1}
      </Label>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <GenericLanguageTaskTypesSelect
          width="100%"
          value={configuration?.type as WorksheetGenericLanguageModuleType}
          onChange={(val) => {
            const newConfiguration: WorksheetModule = {
              ...(configuration || {}),
              type: val,
              size: layout,
              configuration: undefined,
            }
            onConfigurationChanged(newConfiguration)
          }}
          clearableValue={null}
          placeholderColor={theme.colors.gray10}
          error={error}
        />
        <span data-tour={isFirstModule ? "module-regenerate-button" : undefined}>
          <OutlineButton
            leftIcon="sync"
            leftIconProps={{ icon: "sync", color: theme.colors.primary30 }}
            size="small"
            width="100%"
            type="button"
            onClick={onRegenerateModule}
          >
            {translations.regenerate}
          </OutlineButton>
        </span>
      </div>
      {["generic/wordSearch", "generic/crossword"].includes(
        configuration?.type ?? "",
      ) && (
        <>
          <span data-tour={isFirstModule ? "module-configure-button" : undefined}>
            <OutlineButton
              leftIcon="cog"
              leftIconProps={{ icon: "cog", color: theme.colors.primary30 }}
              size="small"
              width="100%"
              type="button"
              onClick={() => setDetailConfigurationIsOpen(true)}
            >
              {translations.configure}
            </OutlineButton>
          </span>
          {detailConfigurationIsOpen && (
            <WordlistModal
              isOpen={true}
              onClose={(_configuration) => {
                setDetailConfigurationIsOpen(false)
                if (_configuration) {
                  const newConfiguration: WorksheetModule = {
                    ...configuration!,
                    configuration: _configuration,
                  }
                  onConfigurationChanged(newConfiguration)
                }
              }}
              pageIndex={pageIndex}
              moduleIndex={moduleIndex}
              layout={layout}
              configuration={configuration!.configuration}
            />
          )}
        </>
      )}
    </Layout>
  )
}

export const GenericLanguageModuleConfigurationCellLayout = Layout

export default GenericLanguageModuleConfigurationCell
